import { StandardEventProperties } from '@betterment/js-runtime';
import { Box, Icon, IconColorToken, ListRow, Text, TextColorToken, Tooltip } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { SambaAutoLink } from '@betterment/shared-components';
import { useColorMode } from '@betterment/shared-components/src/components/color-mode/ColorModeProvider';

import { readOnlyTooltipCopy } from './constants';

import { trackClick } from '../../features/track';
import {
  RecurringTransactionConfigStatus,
  RecurringTransactionConfigStatusStyle,
  ThemedAsset,
} from '../../graphql/schema';

type Props = {
  icon: ThemedAsset;
  transactionUrl: string;
  eventProperties: StandardEventProperties;
  amount?: string;
  destinationAccountName: string;
  transactionDescription: string;
  nextTransactionDate?: string;
  isReadOnly: boolean;
  status?: RecurringTransactionConfigStatus;
};

const TransactionRowContent = (props: Props) => {
  const { colorMode } = useColorMode();
  const {
    icon,
    transactionDescription,
    destinationAccountName,
    amount,
    nextTransactionDate,
    isReadOnly = false,
    status,
  } = props;

  const textColor: Record<RecurringTransactionConfigStatusStyle, TextColorToken> = {
    DANGER: 'text.critical',
    INFO: 'text',
    NONE: 'text',
    SUCCESS: 'text.success',
  };

  const iconColor: Record<RecurringTransactionConfigStatusStyle, IconColorToken> = {
    DANGER: 'icon.critical',
    INFO: 'icon',
    NONE: 'icon',
    SUCCESS: 'icon.success',
  };

  return (
    <ListRow
      css={{
        _groupHover: {
          cursor: 'pointer',
        },
        paddingTop: 'space.150',
        paddingBottom: 'space.150',
      }}
    >
      <ListRow.LeadingAsset css={{ width: '24px' }}>
        <img
          src={icon[colorMode]}
          alt={destinationAccountName}
          className={css({
            width: 'icon.small',
          })}
        />
      </ListRow.LeadingAsset>
      <ListRow.LeadingContent>
        <Text
          variant="text100"
          color="text"
          css={{
            textDecoration: {
              _groupHover: 'underline',
              _groupFocus: 'underline',
            },
          }}
        >
          {destinationAccountName}
        </Text>
        <Text variant="text50" color="text.weak">
          {transactionDescription}
        </Text>
      </ListRow.LeadingContent>
      <ListRow.TrailingAsset>
        <Box css={{ display: 'flex', alignItems: 'center' }}>
          <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginRight: 'space.100' }}>
            {amount && (
              <Text variant="text100" color="text">
                {amount}
              </Text>
            )}
            {nextTransactionDate && (
              <Text variant="text50" color="text.weak">
                {nextTransactionDate}
              </Text>
            )}
          </Box>
          {isReadOnly ? (
            <Tooltip>
              <Tooltip.Trigger asChild={true}>
                <Tooltip.IconButton aria-label="Unable to edit account" name="InfoCircle" />
              </Tooltip.Trigger>
              <Tooltip.Content align="start" side="left">
                {readOnlyTooltipCopy}
              </Tooltip.Content>
            </Tooltip>
          ) : (
            <Icon name="CaretRight" size="small" color="icon" />
          )}
        </Box>
      </ListRow.TrailingAsset>
      {status && (
        <ListRow.BottomContent alignment="inset" css={{ mt: 'space.100', display: 'flex', flexDirection: 'row' }}>
          <Text color={textColor[status.style]}>{status.name}</Text>
          <Icon name="InfoCircle" css={{ ml: 'space.50' }} color={iconColor[status.style]} size="small" />
        </ListRow.BottomContent>
      )}
    </ListRow>
  );
};

export const TransactionRow = (props: Props) => {
  const { transactionUrl, eventProperties, isReadOnly } = props;
  const { name: eventName, ...restEventProperties } = eventProperties || {};

  return (
    <>
      {isReadOnly ? (
        <TransactionRowContent {...props} />
      ) : (
        <Box className="group">
          <SambaAutoLink
            data-testid="transactionRowUrl"
            to={transactionUrl}
            aria-disabled={isReadOnly}
            css={{ color: 'text', textDecoration: 'none!important' }}
            onClick={() => trackClick({ name: eventName, ...restEventProperties })}
          >
            <TransactionRowContent {...props} />
          </SambaAutoLink>
        </Box>
      )}
    </>
  );
};
